import * as RDialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { focusRing } from '../lib/focus'

export interface DrawerProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  title: string
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  /** `end` is the sliding provenance panel (SRE-FR-004); logical, so RTL-safe. */
  side?: 'end' | 'start' | 'bottom'
  width?: 'sm' | 'md' | 'lg'
  trigger?: ReactNode
  className?: string
}

const WIDTHS = { sm: 'sm:max-w-sm', md: 'sm:max-w-md', lg: 'sm:max-w-xl' } as const

/**
 * F0-16 — side drawer.
 *
 * Built on Radix Dialog rather than a bespoke panel so focus trap, focus
 * restore and Esc handling are identical to the modal. Deliberately non-modal
 * in feel but modal in mechanics: a provenance drawer that leaks focus back to
 * the claim feed behind it is an AC-56 failure.
 */
export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  side = 'end',
  width = 'md',
  trigger,
  className,
}: DrawerProps) {
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      {trigger && <RDialog.Trigger asChild>{trigger}</RDialog.Trigger>}
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-drawer bg-surface-scrim data-[state=open]:animate-fade-in" />
        <RDialog.Content
          className={cn(
            'fixed z-drawer flex flex-col border-border-subtle bg-surface-overlay shadow-2xl',
            side === 'end' && 'inset-y-0 end-0 w-full border-s data-[state=open]:animate-slide-in-end',
            side === 'start' && 'inset-y-0 start-0 w-full border-e data-[state=open]:animate-slide-in-end',
            side === 'bottom' && 'inset-x-0 bottom-0 max-h-[85vh] rounded-t-lg border-t data-[state=open]:animate-slide-up',
            side !== 'bottom' && WIDTHS[width],
            className,
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-border-subtle p-5">
            <div className="flex flex-col gap-1">
              <RDialog.Title className="text-h3 text-text-primary">{title}</RDialog.Title>
              {description && (
                <RDialog.Description className="text-body-sm text-text-secondary">
                  {description}
                </RDialog.Description>
              )}
            </div>
            <RDialog.Close asChild>
              <button
                type="button"
                aria-label="Close panel"
                className={cn(
                  'rounded-md p-1 text-text-secondary hover:bg-surface-hover hover:text-text-primary',
                  focusRing,
                )}
              >
                <X aria-hidden className="size-4" />
              </button>
            </RDialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto p-5">{children}</div>

          {footer && (
            <div className="flex items-center justify-end gap-3 border-t border-border-subtle p-5">
              {footer}
            </div>
          )}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  )
}
